import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { _columns } from 'common/components/ActionMenu/Attach/_columns.data';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { useStyles } from 'common/components/ActionMenu/Attach/AttachFormModal/index.styles';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import { PurchaseOrder_purchaseOrder } from 'purchaseOrder/view/__generated__/PurchaseOrder';
import React, { useCallback } from 'react';
import { PurchaseOrderAvailableDocumentTypes } from '../../__generated__/PurchaseOrderAvailableDocumentTypes';
import { toOrderByVariable } from '../utils';
import {
  AttachablePurchaseOrderDocuments,
  AttachablePurchaseOrderDocuments_attachablePurchaseOrderDocuments_nodes,
} from './__generated__/AttachablePurchaseOrderDocuments';
import { AttachPurchaseOrderDocumentVariables } from './__generated__/AttachPurchaseOrderDocument';

import ATTACHABLE_PURCHASE_ORDER_DOCUMENTS from './AttachablePurchaseOrderDocuments.graphql';
import ATTACH_PURCHASE_ORDER_DOCUMENT from './AttachPurchaseOrderDocument.graphql';
import PURCHASE_ORDER from '../../../../../PurchaseOrder.graphql';

type AttachableDataType =
  AttachablePurchaseOrderDocuments_attachablePurchaseOrderDocuments_nodes;
export interface AttachFormProps {
  availableDocumentTypes: PurchaseOrderAvailableDocumentTypes | undefined;
  purchaseOrder: PurchaseOrder_purchaseOrder;
  isDirty: boolean;
}

export const AttachFormModal: React.FC<AttachFormProps> = ({
  availableDocumentTypes,
  purchaseOrder,
  isDirty,
}) => {
  const styles = useStyles();

  const getDocumentTypeOptions = useCallback(
    (data: typeof availableDocumentTypes | undefined) => {
      return (
        data?.purchaseOrderAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
          title:
            !purchaseOrder?.isDraft && doctype.isAccountingDocument
              ? 'Upload prohibited during/after the approval process'
              : doctype.documentType || '',

          disabled:
            (!purchaseOrder?.isDraft && doctype.isAccountingDocument) ||
            undefined,
        })) || []
      );
    },
    [purchaseOrder]
  );

  const canSelectItem = useCallback(
    (item: AttachableDataType) =>
      purchaseOrder?.isDraft ? true : !item._isAccountingDocument!,
    [purchaseOrder]
  );

  const transformData = useCallback(
    (data: AttachablePurchaseOrderDocuments | undefined) => {
      return data?.attachablePurchaseOrderDocuments?.nodes || [];
    },
    []
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          purchaseOrderId: entityId,
          entityDocumentId: entityDocIds,
          documentTypeId: null, // Will be set via onDocumentTypeChange
        },
      } as AttachPurchaseOrderDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const disableLogic = purchaseOrder?.isDraft
        ? false
        : item._isAccountingDocument;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;
      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout disabled={disableLogic!} item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.documentTypes?.documentType}
              </Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.currency?.isoCode}
              </Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {fieldContent}
              </Text>
            </Stack>
          );
      }
    },
    [styles, purchaseOrder]
  );

  return (
    <GenericAttachForm
      entity={{
        id: purchaseOrder.id!,
        isDraft: purchaseOrder.isDraft,
      }}
      isDirty={isDirty}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_PURCHASE_ORDER_DOCUMENTS}
      attachDocumentsMutation={ATTACH_PURCHASE_ORDER_DOCUMENT}
      detailsQuery={PURCHASE_ORDER}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        purchaseOrderId: purchaseOrder.id!,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
